import { collection, deleteField, doc, DocumentData, getDocs, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';

import { logAnalyticsEvent } from '../analytics/analytics';
import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import { EventScore, EVENT_SCORE_STATUSES } from '../types/EventScore';
import { planRowScoreCleanup, ScoreRowPlayers } from '../utils/eventScoreUtils';
import { effectiveStrokeCount } from '../utils/handicapUtils';

import type EventDay from '../types/EventDay';

export const useEventScoreMutator = () => {
  const { isLeagueAdmin, isOrgAdmin, userState } = useData();
  const { isGod } = useAuth();

  const isAuthorized = (orgId: string, leagueId: string): boolean => {
    if (userState.status !== 'ready' || !userState.userProfile) return false;
    return isGod || isLeagueAdmin(leagueId, orgId) || isOrgAdmin(orgId);
  };

  const upsertEventScore = async (
    orgId: string,
    eventId: string,
    leagueId: string,
    score: EventScore
  ): Promise<EventScore | null> => {
    if (!isAuthorized(orgId, leagueId)) {
      console.error('Permission denied');
      return null;
    }

    // USGA posting state is written only through its own request path (and by the server) — never
    // by a score save, or a stale in-memory copy would overwrite a newer post.
    const { usgaPost: _usgaPost, ...scoreFields } = score;

    try {
      // Use caller-supplied total when non-zero (pre-computed with course data for X holes).
      // Otherwise compute from scoreByHole: x{n} contributes n, bare X contributes 0.
      const totalScore = score.totalScore !== 0
        ? score.totalScore
        : score.scoreByHole.reduce((sum, n) => sum + (effectiveStrokeCount(n) ?? 0), 0);

      // New score: generate itemId and set createdAt
      if (!score.itemId) {
        const scoreRef = doc(collection(firestore, `org/${orgId}/event/${eventId}/score`));
        const newScore: EventScore = {
          ...scoreFields,
          itemId: scoreRef.id,
          totalScore,
          status: EVENT_SCORE_STATUSES.DRAFT,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        await setDoc(scoreRef, newScore);
        logAnalyticsEvent('score_submitted', { org_id: orgId, event_id: eventId });
        return newScore;
      }

      // Existing score: update
      const scoreRef = doc(firestore, `org/${orgId}/event/${eventId}/score/${score.itemId}`);
      const updates = {
        ...scoreFields,
        totalScore,
        updatedAt: serverTimestamp(),
      };
      await setDoc(scoreRef, updates, { merge: true });
      return { ...score, totalScore };
    } catch (error) {
      console.error('Error upserting score:', error);
      return null;
    }
  };

  const publishEventScores = async (
    orgId: string,
    eventId: string,
    leagueId: string,
    dayIndex: number,
    currentDays: EventDay[],
  ): Promise<void> => {
    if (!isAuthorized(orgId, leagueId)) {
      console.error('Permission denied');
      return;
    }

    try {
      const eventRef = doc(firestore, `org/${orgId}/event/${eventId}`);
      const scoresSnapshot = await getDocs(query(
        collection(firestore, `org/${orgId}/event/${eventId}/score`),
        where('status', '==', EVENT_SCORE_STATUSES.DRAFT),
        where('dayIndex', '==', dayIndex),
      ));

      const updatedDays = [...currentDays];
      updatedDays[dayIndex] = { ...updatedDays[dayIndex], scoreStatus: EVENT_SCORE_STATUSES.PUBLISHED };

      const batch = writeBatch(firestore);
      const publishedAt = serverTimestamp();
      scoresSnapshot.docs.forEach((d) => {
        batch.update(d.ref, { status: EVENT_SCORE_STATUSES.PUBLISHED, publishedAt, updatedAt: publishedAt });
      });
      batch.update(eventRef, { days: updatedDays });
      await batch.commit();
    } catch (error) {
      console.error('Error publishing scores:', error);
    }
  };

  const unpublishEventScores = async (
    orgId: string,
    eventId: string,
    leagueId: string,
    dayIndex: number,
    currentDays: EventDay[],
  ): Promise<void> => {
    if (!isAuthorized(orgId, leagueId)) {
      console.error('Permission denied');
      return;
    }

    try {
      const eventRef = doc(firestore, `org/${orgId}/event/${eventId}`);
      const scoresSnapshot = await getDocs(query(
        collection(firestore, `org/${orgId}/event/${eventId}/score`),
        where('status', '==', EVENT_SCORE_STATUSES.PUBLISHED),
        where('dayIndex', '==', dayIndex),
      ));

      const updatedDays = [...currentDays];
      updatedDays[dayIndex] = { ...updatedDays[dayIndex], scoreStatus: EVENT_SCORE_STATUSES.DRAFT };

      const batch = writeBatch(firestore);
      const updatedAt = serverTimestamp();
      scoresSnapshot.docs.forEach((d) => {
        batch.update(d.ref, { status: EVENT_SCORE_STATUSES.DRAFT, updatedAt });
      });
      batch.update(eventRef, { days: updatedDays });
      await batch.commit();
    } catch (error) {
      console.error('Error unpublishing scores:', error);
    }
  };

  const deleteEventScore = async (
    orgId: string,
    eventId: string,
    leagueId: string,
    itemId: string
  ): Promise<void> => {
    if (!isAuthorized(orgId, leagueId)) {
      console.error('Permission denied');
      return;
    }

    try {
      const scoreRef = doc(firestore, `org/${orgId}/event/${eventId}/score/${itemId}`);
      await updateDoc(scoreRef, { status: EVENT_SCORE_STATUSES.DELETED, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error deleting score:', error);
    }
  };

  // THE way to save a score-entry row's doc (build it with buildEventScore). Saves `score`, then
  // retires/trims any other live doc still naming one of the row's players (planRowScoreCleanup),
  // so no player is ever left in two docs of one scoring type, or in a doc missing another member of
  // their row. `liveScores`: the caller's current live docs for the day (duplicates are fine).
  const saveRowScore = async (
    orgId: string,
    eventId: string,
    leagueId: string,
    row: ScoreRowPlayers,
    score: EventScore,
    liveScores: EventScore[],
  ): Promise<EventScore | null> => {
    const saved = await upsertEventScore(orgId, eventId, leagueId, score);
    if (!saved) return null;
    const { retire, trim } = planRowScoreCleanup(row, liveScores, saved.itemId);
    await Promise.all([
      ...retire.map((s) => deleteEventScore(orgId, eventId, leagueId, s.itemId)),
      ...trim.map((s) => upsertEventScore(orgId, eventId, leagueId, s)),
    ]);
    return saved;
  };

  const setScoreOverride = async (
    orgId: string,
    eventId: string,
    scoreId: string,
    overrides: {
      ghinIndex?: number;
      playingHandicap?: number;
      netScoreByHole?: Record<string, number>;
    },
  ): Promise<void> => {
    if (!userState.userProfile) return;
    const scoreRef = doc(firestore, `org/${orgId}/event/${eventId}/score/${scoreId}`);
    const updates: DocumentData = { updatedAt: serverTimestamp() };
    if (overrides.ghinIndex !== undefined) updates.ghinIndexOverride = overrides.ghinIndex;
    if (overrides.playingHandicap !== undefined) updates.playingHandicapOverride = overrides.playingHandicap;
    if (overrides.netScoreByHole !== undefined) updates.netScoreByHoleOverride = overrides.netScoreByHole;
    updates.overrideBy = userState.userProfile.itemId;
    updates.overrideAt = serverTimestamp();
    await updateDoc(scoreRef, updates);
  };

  const clearScoreOverride = async (
    orgId: string,
    eventId: string,
    scoreId: string,
    fields?: ('ghinIndex' | 'playingHandicap' | 'netScoreByHole')[],
  ): Promise<void> => {
    const scoreRef = doc(firestore, `org/${orgId}/event/${eventId}/score/${scoreId}`);
    const updates: DocumentData = { updatedAt: serverTimestamp() };
    const toClear = fields ?? ['ghinIndex', 'playingHandicap', 'netScoreByHole'];
    if (toClear.includes('ghinIndex')) updates.ghinIndexOverride = deleteField();
    if (toClear.includes('playingHandicap')) updates.playingHandicapOverride = deleteField();
    if (toClear.includes('netScoreByHole')) updates.netScoreByHoleOverride = deleteField();
    if (toClear.length === 3) {
      updates.overrideBy = deleteField();
      updates.overrideAt = deleteField();
    }
    await updateDoc(scoreRef, updates);
  };

  // upsertEventScore is deliberately not exported: saveRowScore is the only way to write a score doc.
  return { saveRowScore, publishEventScores, unpublishEventScores, deleteEventScore, setScoreOverride, clearScoreOverride };
};
